import { useState } from 'react';

import { format } from 'date-fns';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { DatePicker } from '@components/datepicker';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { toQueryString } from '@utils/requests';

import { useAuthenticatedUser } from 'features/authentication';
import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

type FieldConfig = { label: string; checked: boolean };

type Query = {
  state: string[];
  date_published__gte?: string;
  date_published__lte?: string;
};

function useFieldState(initialState: Record<string, FieldConfig>) {
  const [state, setState] = useState(initialState);
  const list = Object.entries(state).map((e) => ({
    id: e[0],
    ...e[1],
  })) as Array<FieldConfig & { id: keyof typeof state }>;

  function onChange(fieldId: keyof typeof state) {
    let newState = Object.assign({}, state);
    newState[fieldId].checked = !newState[fieldId].checked;
    setState(newState);
  }

  const selectedFields = Object.keys(state).filter((key) => state[key].checked);

  const selectedAll = selectedFields.length === list.length;

  const selectedSome = selectedFields.length > 0;

  const selectAll = () => {
    let newState = Object.assign({}, state);
    Object.keys(state).forEach((key) => (newState[key].checked = !selectedAll));
    setState(newState);
  };

  return {
    state,
    list,
    onChange,
    selectedAll,
    selectedSome,
    selectAll,
    selectedFields,
  };
}

export const ExportJobs = ({ closeModal }: ModalProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { api, t, lang } = useStore();
  const { showError } = useToaster();
  const me = useAuthenticatedUser();

  const [publishedDateFrom, setPublishedDateFrom] = useState<Date | undefined>(undefined);
  const [publishedDateTo, setPublishedDateTo] = useState<Date | undefined>(undefined);

  const statusFields = useFieldState({
    published: { label: t('exportjobsmodal.filter.status.published'), checked: false },
    expired: { label: t('exportjobsmodal.filter.status.expired'), checked: false },
    drafts: { label: t('exportjobsmodal.filter.status.drafts'), checked: false },
    archived: { label: t('exportjobsmodal.filter.status.archived'), checked: false },
  });

  const detailFields = useFieldState({
    id: { label: t('exportjobsmodal.fields.id'), checked: false },
    department: { label: t('exportjobsmodal.fields.department'), checked: false },
    title: { label: t('exportjobsmodal.fields.title'), checked: false },
    location: { label: t('exportjobsmodal.fields.location'), checked: false },
    work_type: { label: t('exportjobsmodal.fields.worktype'), checked: false },
    status: { label: t('exportjobsmodal.fields.status'), checked: false },
  });

  const dateFields = useFieldState({
    date_created: { label: t('exportjobsmodal.fields.datecreated'), checked: false },
    date_published: { label: t('exportjobsmodal.fields.datepublished'), checked: false },
    deadline: { label: t('exportjobsmodal.fields.deadline'), checked: false },
  });

  const applicantStatisticsFields = useFieldState({
    total: { label: t('exportjobsmodal.fields.numapplicants'), checked: false },
    hired: { label: t('exportjobsmodal.fields.hiredapplicants'), checked: false },
    rejected: { label: t('exportjobsmodal.fields.rejectedapplicants'), checked: false },
    retracted: { label: t('exportjobsmodal.fields.retractedapplications'), checked: false },
  });

  async function onSubmit() {
    setSubmitting(true);
    let checkedFields = [
      ...detailFields.selectedFields,
      ...dateFields.selectedFields,
      ...applicantStatisticsFields.selectedFields,
    ];
    let query: Query = { state: [] };

    if (statusFields.state.published.checked) {
      query.state.push('Published');
    }
    if (statusFields.state.archived.checked) {
      query.state.push('Archived');
    }
    if (statusFields.state.drafts.checked) {
      query.state.push('Draft');
    }
    if (statusFields.state.expired.checked) {
      query.state.push('Expired');
    }

    if (publishedDateFrom) {
      query.date_published__gte = format(publishedDateFrom, 'yyyy-MM-dd');
    }
    if (publishedDateTo) {
      query.date_published__lte = format(publishedDateTo, 'yyyy-MM-dd');
    }

    const response = await api.jobs.exportJobs(toQueryString(query), { fields: checkedFields });
    if (!response.error) {
      setSubmitted(true);
    } else {
      showError(t('exportjobsmodal.submit.error'));
    }
    setSubmitting(false);
  }

  if (submitted) {
    return (
      <Modal>
        <Modal.Header text={t('exportjobsmodal.submitted.title')} />

        <Stack space={4}>
          <Text
            text={
              <>
                {t('exportjobsmodal.submitted.confirmtext.sent')} <strong>{me.email}</strong>{' '}
                {t('exportjobsmodal.submitted.confirmtext.ready')}
              </>
            }
          />
          <Text text={t('exportjobsmodal.submitted.notice')} />
        </Stack>
        <Modal.Actions>
          <Button
            onClick={closeModal}
            variant="secondary"
            text={t('exportjobsmodal.button.close')}
          />
        </Modal.Actions>
      </Modal>
    );
  }
  return (
    <Modal>
      <Modal.Header text={t('exportjobsmodal.title')} />
      <Stack space={4}>
        <DatePicker
          lang={lang}
          label={t('exportjobsmodal.filter.publisheddate.from')}
          value={publishedDateFrom}
          onChange={setPublishedDateFrom}
        />
        <DatePicker
          lang={lang}
          label={t('exportjobsmodal.filter.publisheddate.to')}
          value={publishedDateTo}
          onChange={setPublishedDateTo}
        />
      </Stack>
      <Stack space={2}>
        <Checkbox
          label={t('exportjobsmodal.filter.status.header')}
          labelFont={'bold14'}
          value={statusFields.selectedAll ? true : statusFields.selectedSome ? 'mixed' : false}
          onChange={statusFields.selectAll}
        />
        {statusFields.list.map((field) => (
          <Checkbox
            key={field.id}
            label={field.label}
            value={field.checked}
            onChange={() => statusFields.onChange(field.id)}
          />
        ))}
      </Stack>
      <Text font="bold18" text={t('exportjobsmodal.labels.headers')} />
      <Stack space={2}>
        <Checkbox
          label={t('exportjobsmodal.fields.details')}
          labelFont={'bold14'}
          value={detailFields.selectedAll ? true : detailFields.selectedSome ? 'mixed' : false}
          onChange={detailFields.selectAll}
        />
        {detailFields.list.map((field) => (
          <Checkbox
            key={field.id}
            label={field.label}
            value={field.checked}
            onChange={() => detailFields.onChange(field.id)}
          />
        ))}
      </Stack>
      <Stack space={2}>
        <Checkbox
          label={t('exportjobsmodal.fields.dates')}
          labelFont={'bold14'}
          value={dateFields.selectedAll ? true : dateFields.selectedSome ? 'mixed' : false}
          onChange={dateFields.selectAll}
        />
        {dateFields.list.map((field) => (
          <Checkbox
            key={field.id}
            label={field.label}
            value={field.checked}
            onChange={() => dateFields.onChange(field.id)}
          />
        ))}
      </Stack>
      <Stack space={2}>
        <Checkbox
          label={t('exportjobsmodal.fields.applicantstatistics')}
          labelFont={'bold14'}
          value={
            applicantStatisticsFields.selectedAll
              ? true
              : applicantStatisticsFields.selectedSome
              ? 'mixed'
              : false
          }
          onChange={applicantStatisticsFields.selectAll}
        />
        {applicantStatisticsFields.list.map((field) => (
          <Checkbox
            key={field.id}
            label={field.label}
            value={field.checked}
            onChange={() => applicantStatisticsFields.onChange(field.id)}
          />
        ))}
      </Stack>
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={closeModal}
          text={t('exportjobsmodal.button.cancel')}
        />
        <Button
          onClick={onSubmit}
          isDisabled={
            submitting ||
            !(
              detailFields.selectedSome ||
              dateFields.selectedSome ||
              applicantStatisticsFields.selectedSome
            )
          }
          text={t('exportjobsmodal.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
};
